<template>
  <app-content class="admin-panel">
    <b-tabs v-model="tabIndex">
      <b-tab
        v-for="(tab, index) in permittedTabs"
        :active="tab.active && tab.canAccess"
        :key="tab.name"
        :title="tab.title"
      >
        <component
          v-if="componentHandler(index)"
          :is="tab.component"
        />
      </b-tab>
    </b-tabs>
  </app-content>
</template>

<script>
import router from '@/router';

const AlarmConfigurations = () => import(/* webpackChunkName: "adminPanelAlarmConfigurationsComponent" */ './alarmConfigurations');
const DiagnosticsConfigurations = () => import(/* webpackChunkName: "adminPanelDiagnosticsConfigurationsComponent" */ './diagnosticsConfigurations');
const InstallationList = () => import(/* webpackChunkName: "adminPanelInstallationComponent" */ './installation');
const ProfileTab = () => import(/* webpackChunkName: "adminPanelProfileComponent" */ './profile');
const RegionalList = () => import(/* webpackChunkName: "adminPanelRegionalComponent" */ './regional');
const UserList = () => import(/* webpackChunkName: "adminPanelUserComponent" */ './user');

const TAB_TITLE_PREFIX = 'admin-panel.tabs-titles';
const TAB_USERS = 'users';
const TAB_PROFILES = 'profiles';
const TAB_REGIONAL = 'regional';
const TAB_INSTALLATIONS = 'installations';
const TAB_ALARM_CONFIGURATIONS = 'alarm-configurations';
const TAB_DIAGNOSTICS_CONFIGURATIONS = 'diagnostics-configurations';

export default {
  name: 'AdminPanel',
  props: {
    activeTabName: {
      default: 'users',
    },
  },
  components: {
    AlarmConfigurations,
    DiagnosticsConfigurations,
    InstallationList,
    ProfileTab,
    RegionalList,
    UserList,
  },
  created() {
    this.setActiveTab();

    const requiredPermission = [
      'PER002',
      'PER004',
      'PER009',
      'PER011',
      'PER019',
      'PER020',
    ];

    if (!this.$hasUserAnyPermission(requiredPermission)) {
      router.push({ name: 'dashboard' });
    }
  },
  data: () => ({
    tabIndex: 0,
  }),
  computed: {
    permittedTabs() {
      return this.tabs.filter(({ canAccess }) => canAccess);
    },
    tabs() {
      return [
        {
          active: this.activeTabName === TAB_USERS,
          name: TAB_USERS,
          component: UserList,
          canAccess: this.$hasUserPermission('PER011'),
          title: this.$t(`${TAB_TITLE_PREFIX}.${TAB_USERS}`),
        },
        {
          active: this.activeTabName === TAB_PROFILES,
          name: TAB_PROFILES,
          component: ProfileTab,
          canAccess: this.$hasUserPermission('PER002'),
          title: this.$t(`${TAB_TITLE_PREFIX}.${TAB_PROFILES}`),
        },
        {
          active: this.activeTabName === TAB_REGIONAL,
          name: TAB_REGIONAL,
          component: RegionalList,
          canAccess: this.$hasUserPermission('PER009'),
          title: this.$t(`${TAB_TITLE_PREFIX}.${TAB_REGIONAL}`),
        },
        {
          active: this.activeTabName === TAB_INSTALLATIONS,
          name: TAB_INSTALLATIONS,
          component: InstallationList,
          canAccess: this.$hasUserPermission('PER020'),
          title: this.$t(`${TAB_TITLE_PREFIX}.${TAB_INSTALLATIONS}`),
        },
        {
          active: this.activeTabName === TAB_ALARM_CONFIGURATIONS,
          name: TAB_ALARM_CONFIGURATIONS,
          component: AlarmConfigurations,
          canAccess: this.$hasUserPermission('PER004'),
          title: this.$t(`${TAB_TITLE_PREFIX}.${TAB_ALARM_CONFIGURATIONS}`),
        },
        {
          active: this.activeTabName === TAB_DIAGNOSTICS_CONFIGURATIONS,
          name: TAB_DIAGNOSTICS_CONFIGURATIONS,
          component: DiagnosticsConfigurations,
          canAccess: this.$hasUserPermission('PER019'),
          title: this.$t(`${TAB_TITLE_PREFIX}.${TAB_DIAGNOSTICS_CONFIGURATIONS}`),
        },
      ];
    },
  },
  methods: {
    componentHandler(index) {
      return this.tabIndex === index;
    },
    setActiveTab() {
      if (this.$route.params.activeTabName) {
        this.tabIndex = this.tabs.findIndex(tab => tab.name === this.$route.params.activeTabName);
      }
    },
  },
};
</script>

<style lang="sass" scoped>
.admin-panel
  ::v-deep .app-content__main-content
    max-width: 100%
</style>
